import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Target, TrendingUp, Calendar, ChevronRight, ExternalLink, Trophy, Sparkles, Plus, Loader2 } from 'lucide-react';
import { format, differenceInDays, isWithinInterval, parseISO, addMonths, addQuarters, addYears, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AnimatedProgress } from '@/components/ui/animated-progress';
import { CircularProgress } from '@/components/ui/circular-progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import AnimatedCounter from '@/components/ui/animated-counter';

interface GoalStatusWidgetProps {
  agencyId?: string;
  className?: string;
  compact?: boolean;
}

interface CostSavingsGoal {
  id: string;
  name: string;
  target_amount: number;
  period_type: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
}

type PeriodType = 'monthly' | 'quarterly' | 'yearly';

const periodPresets: Record<PeriodType, { label: string; getDates: () => { start: Date; end: Date } }> = {
  monthly: {
    label: 'Monthly',
    getDates: () => ({ start: startOfMonth(new Date()), end: endOfMonth(new Date()) })
  },
  quarterly: {
    label: 'Quarterly',
    getDates: () => ({ start: startOfQuarter(new Date()), end: endOfQuarter(new Date()) })
  },
  yearly: {
    label: 'Yearly',
    getDates: () => ({ start: startOfYear(new Date()), end: endOfYear(new Date()) })
  }
};

export default function GoalStatusWidget({ agencyId, className, compact = false }: GoalStatusWidgetProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAdminAuth();
  
  // Form state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [goalName, setGoalName] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [periodType, setPeriodType] = useState<PeriodType>('monthly');
  
  // Navigate to analytics page with goals section
  const handleNavigateToGoals = () => {
    const params = agencyId ? `?agency=${agencyId}` : '';
    navigate(`/admin/analytics${params}`);
  };

  // Reset form
  const resetForm = () => {
    setGoalName('');
    setTargetAmount('');
    setPeriodType('monthly');
  };

  // Handle goal creation
  const handleCreateGoal = async () => {
    if (!goalName.trim()) {
      toast.error('Please enter a goal name');
      return;
    }
    
    const amount = parseFloat(targetAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid target amount');
      return;
    }

    if (!agencyId) {
      toast.error('Agency ID is required to create a goal');
      return;
    }

    setIsCreating(true);
    
    try {
      const dates = periodPresets[periodType].getDates();
      
      const { error } = await supabase
        .from('cost_savings_goals')
        .insert({
          agency_id: agencyId,
          name: goalName.trim(),
          target_amount: amount,
          period_type: periodType,
          start_date: format(dates.start, 'yyyy-MM-dd'),
          end_date: format(dates.end, 'yyyy-MM-dd'),
          is_active: true,
          created_by: user?.id
        });

      if (error) throw error;

      toast.success('Goal created successfully!');
      setDialogOpen(false);
      resetForm();
      
      // Refresh goals
      queryClient.invalidateQueries({ queryKey: ['cost-savings-goals-status'] });
    } catch (error) {
      console.error('Error creating goal:', error);
      toast.error('Failed to create goal');
    } finally {
      setIsCreating(false);
    }
  };

  // Fetch active goals
  const { data: goals, isLoading: goalsLoading } = useQuery({
    queryKey: ['cost-savings-goals-status', agencyId],
    queryFn: async () => {
      let query = supabase
        .from('cost_savings_goals')
        .select('*')
        .eq('is_active', true)
        .order('end_date', { ascending: true });

      if (agencyId) {
        query = query.eq('agency_id', agencyId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as CostSavingsGoal[];
    }
  });

  // Fetch current savings for active goals
  const { data: currentSavings, isLoading: savingsLoading } = useQuery({
    queryKey: ['current-cost-savings', agencyId, goals?.map(g => g.id)],
    queryFn: async () => {
      if (!goals || goals.length === 0) return {};

      const savingsMap: Record<string, number> = {};

      for (const goal of goals) {
        let query = supabase
          .from('triage_sessions')
          .select('recommendation, completed_at')
          .not('recommendation', 'is', null)
          .gte('completed_at', goal.start_date)
          .lte('completed_at', goal.end_date);

        if (agencyId) {
          query = query.eq('agency_id', agencyId);
        }

        const { data: sessions } = await query;

        // Calculate savings based on recommendations (simplified calculation)
        const savingsPerRecommendation: Record<string, number> = {
          'HOME': 2350,
          'PCP': 2200,
          'URGENT_CARE': 1500,
          'ER_NOW': 0
        };

        const totalSavings = (sessions || []).reduce((sum, session) => {
          return sum + (savingsPerRecommendation[session.recommendation as string] || 0);
        }, 0);

        savingsMap[goal.id] = totalSavings;
      }

      return savingsMap;
    },
    enabled: !!goals && goals.length > 0
  });

  const isLoading = goalsLoading || savingsLoading;

  // Filter to show only current/upcoming goals
  const activeGoals = goals?.filter(goal => {
    const today = new Date();
    const endDate = parseISO(goal.end_date);
    return endDate >= today;
  }).slice(0, compact ? 2 : 4) || [];

  // Quick create form component
  const QuickCreateDialog = () => (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1.5">
          <Plus className="h-4 w-4" />
          Create Goal
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Quick Create Goal
          </DialogTitle>
          <DialogDescription>
            Set a cost savings target to track your agency's progress.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="goal-name">Goal Name</Label>
            <Input
              id="goal-name"
              placeholder="e.g., Q1 2025 Savings Target"
              value={goalName}
              onChange={(e) => setGoalName(e.target.value)}
              maxLength={100}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="target-amount">Target Amount ($)</Label>
            <Input
              id="target-amount"
              type="number"
              placeholder="e.g., 50000"
              value={targetAmount}
              onChange={(e) => setTargetAmount(e.target.value)}
              min={0}
            />
            <p className="text-xs text-muted-foreground">
              The total cost savings you want to achieve
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="period-type">Time Period</Label>
            <Select value={periodType} onValueChange={(v) => setPeriodType(v as PeriodType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">
                  <div className="flex flex-col items-start">
                    <span>Monthly</span>
                    <span className="text-xs text-muted-foreground">Current month</span>
                  </div>
                </SelectItem>
                <SelectItem value="quarterly">
                  <div className="flex flex-col items-start">
                    <span>Quarterly</span>
                    <span className="text-xs text-muted-foreground">Current quarter</span>
                  </div>
                </SelectItem>
                <SelectItem value="yearly">
                  <div className="flex flex-col items-start">
                    <span>Yearly</span>
                    <span className="text-xs text-muted-foreground">Current year</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            {periodType && (
              <p className="text-xs text-muted-foreground">
                {format(periodPresets[periodType].getDates().start, 'MMM d, yyyy')} - {format(periodPresets[periodType].getDates().end, 'MMM d, yyyy')}
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleCreateGoal} disabled={isCreating}>
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Create Goal
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (isLoading) {
    return (
      <Card className={cn('bg-white border-gray-200', className)}>
        <CardHeader className="pb-2">
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!activeGoals || activeGoals.length === 0) {
    return (
      <Card className={cn('bg-white border-gray-200', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2 text-gray-900">
            <Target className="h-4 w-4 text-[#1F4E79]" />
            Cost Savings Goals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-4 text-center">
            <div className="p-3 rounded-full bg-[#1F4E79]/10 mb-3">
              <Target className="h-6 w-6 text-[#1F4E79]" />
            </div>
            <p className="text-sm font-medium text-gray-900">No active goals</p>
            <p className="text-xs text-gray-600 mb-4">Set cost savings targets to track your agency's progress</p>
            {agencyId ? (
              <QuickCreateDialog />
            ) : (
              <Button size="sm" onClick={handleNavigateToGoals} className="gap-1.5">
                <Plus className="h-4 w-4" />
                Create Goal
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('bg-white border-gray-200', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2 text-gray-900">
          <Target className="h-4 w-4 text-[#1F4E79]" />
          Cost Savings Goals
          <Badge variant="secondary" className="ml-auto text-xs bg-gray-100 text-gray-700 border-gray-300">
            {activeGoals.length} active
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {activeGoals.map((goal) => {
          const current = currentSavings?.[goal.id] || 0;
          const progress = Math.min(100, Math.round((current / goal.target_amount) * 100));
          const isOnTrack = progress >= 50;
          const isCompleted = progress >= 100;
          const today = new Date();
          const startDate = parseISO(goal.start_date);
          const endDate = parseISO(goal.end_date);
          const daysRemaining = differenceInDays(endDate, today);
          const isCurrentPeriod = isWithinInterval(today, { start: startDate, end: endDate });

          return (
            <button 
              key={goal.id}
              onClick={handleNavigateToGoals}
              className={cn(
                'w-full text-left p-3 rounded-lg border border-gray-200 bg-gray-50 space-y-2 transition-all relative overflow-hidden',
                'hover:bg-gray-100 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#1F4E79]/20',
                isCurrentPeriod && !isCompleted && 'border-[#1F4E79]/30 bg-[#1F4E79]/5 hover:bg-[#1F4E79]/10',
                isCompleted && 'border-[#1E8449]/50 bg-[#1E8449]/10 animate-celebration-glow hover:bg-[#1E8449]/15'
              )}
            >
              {/* Celebration sparkles for completed goals */}
              {isCompleted && (
                <div className="absolute inset-0 pointer-events-none">
                  <Sparkles className="absolute top-1 right-1 h-3 w-3 text-[#1E8449] animate-celebration-sparkle" />
                  <Sparkles className="absolute bottom-1 left-1 h-2.5 w-2.5 text-[#1E8449]/70 animate-celebration-sparkle" style={{ animationDelay: '0.5s' }} />
                  <Sparkles className="absolute top-1/2 right-3 h-2 w-2 text-[#1E8449]/50 animate-celebration-sparkle" style={{ animationDelay: '1s' }} />
                </div>
              )}
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate text-gray-900">{goal.name}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-600 mt-0.5">
                    <Calendar className="h-3 w-3" />
                    <span>{format(startDate, 'MMM d')} - {format(endDate, 'MMM d, yyyy')}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {isCompleted ? (
                    <Badge className="text-xs bg-[#1E8449]/20 text-[#1E8449] border-[#1E8449]/30 animate-celebration-sparkle">
                      <Trophy className="h-3 w-3 mr-1" />
                      Goal Met!
                    </Badge>
                  ) : (
                    <Badge 
                      variant={isOnTrack ? 'default' : 'secondary'}
                      className={cn(
                        'text-xs shrink-0',
                        isOnTrack ? 'bg-[#1E8449]/15 text-[#1E8449] border-[#1E8449]/30' : 'bg-[#B9770E]/15 text-[#B9770E] border-[#B9770E]/30'
                      )}
                    >
                      {isOnTrack ? 'On Track' : 'Behind'}
                    </Badge>
                  )}
                  <ExternalLink className="h-3 w-3 text-gray-500" />
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">
                    $<AnimatedCounter value={current} duration={1000} /> of ${goal.target_amount.toLocaleString()}
                  </span>
                  <span className={cn(
                    'font-medium tabular-nums',
                    progress >= 100 ? 'text-[#1E8449]' : progress >= 50 ? 'text-[#1F4E79]' : 'text-[#B9770E]'
                  )}>
                    <AnimatedCounter value={progress} duration={1000} />%
                  </span>
                </div>
                <AnimatedProgress 
                  value={progress} 
                  duration={1200}
                  className="h-1.5"
                  indicatorClassName={cn(
                    progress >= 100 ? 'bg-[#1E8449]' : progress >= 50 ? 'bg-[#1F4E79]' : 'bg-[#B9770E]'
                  )}
                />
              </div>

              {!compact && (
                <div className="flex items-center justify-between pt-1 text-xs text-gray-600">
                  <div className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    <span>{goal.period_type}</span>
                  </div>
                  {daysRemaining > 0 ? (
                    <span>{daysRemaining} days remaining</span>
                  ) : (
                    <span className="text-[#1E8449]">Completed</span>
                  )}
                </div>
              )}
            </button>
          );
        })}

        <div className="flex items-center justify-between pt-1">
          {goals && goals.length > activeGoals.length && (
            <button 
              onClick={handleNavigateToGoals}
              className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 transition-colors"
            >
              <span>View all {goals.length} goals</span>
              <ChevronRight className="h-3 w-3" />
            </button>
          )}
          {agencyId && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 ml-auto">
                  <Plus className="h-3 w-3" />
                  Add Goal
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[400px]">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-primary" />
                    Quick Create Goal
                  </DialogTitle>
                  <DialogDescription>
                    Set a cost savings target to track your agency's progress.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="goal-name-2">Goal Name</Label>
                    <Input
                      id="goal-name-2"
                      placeholder="e.g., Q1 2025 Savings Target"
                      value={goalName}
                      onChange={(e) => setGoalName(e.target.value)}
                      maxLength={100}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="target-amount-2">Target Amount ($)</Label>
                    <Input
                      id="target-amount-2"
                      type="number"
                      placeholder="e.g., 50000"
                      value={targetAmount}
                      onChange={(e) => setTargetAmount(e.target.value)}
                      min={0}
                    />
                    <p className="text-xs text-muted-foreground">
                      The total cost savings you want to achieve
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="period-type-2">Time Period</Label>
                    <Select value={periodType} onValueChange={(v) => setPeriodType(v as PeriodType)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly (Current month)</SelectItem>
                        <SelectItem value="quarterly">Quarterly (Current quarter)</SelectItem>
                        <SelectItem value="yearly">Yearly (Current year)</SelectItem>
                      </SelectContent>
                    </Select>
                    {periodType && (
                      <p className="text-xs text-muted-foreground">
                        {format(periodPresets[periodType].getDates().start, 'MMM d, yyyy')} - {format(periodPresets[periodType].getDates().end, 'MMM d, yyyy')}
                      </p>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isCreating}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateGoal} disabled={isCreating}>
                    {isCreating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Create Goal
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
